import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  FileText, 
  Search,
  DollarSign,
  TrendingUp,
  X,
  UserPlus,
  Send,
  CheckCircle2,
  Home,
  Download,
  RefreshCw,
  HardDrive,
  Receipt,
  CreditCard,
  MessageSquare,
  ArrowUp,
  Activity as ActivityIcon,
  Trophy
} from "lucide-react";
import { Button } from "../components/ui/button";
import PromiseToPayModal from "../components/PromiseToPayModal";
import UpcomingPaymentsTile from "../components/UpcomingPaymentsTile";
import ActivityLast24Card from "../components/ActivityLast24Card";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Dashboard() {
  // Promise to Pay Modal
  const [showPromiseToPayModal, setShowPromiseToPayModal] = useState(false);
  
  // Client Intake Modal State
  const [showIntakeModal, setShowIntakeModal] = useState(false);
  const [intakeEmail, setIntakeEmail] = useState('');
  const [intakeName, setIntakeName] = useState('');
  const [sendingIntake, setSendingIntake] = useState(false);
  const [intakeSuccess, setIntakeSuccess] = useState(false);
  const [intakeError, setIntakeError] = useState('');

  // Backup State
  const [showBackupModal, setShowBackupModal] = useState(false);
  const [backupInfo, setBackupInfo] = useState(null);
  const [generatingBackup, setGeneratingBackup] = useState(false);
  const [backupMessage, setBackupMessage] = useState('');

  // Client Updates dashboard tile — unread count + avg bureau score deltas
  const [cuStats, setCuStats] = useState(null);
  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const res = await axios.get(`${API}/client-updates/stats`);
        if (alive) setCuStats(res.data);
      } catch (e) { /* silent — tile just hides the badge */ }
    };
    load();
    // Poll every 60s so a new submission bumps the badge without refresh.
    const t = setInterval(load, 60000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  // Fetch backup info
  const fetchBackupInfo = async () => {
    try {
      const res = await axios.get(`${API}/backups/info`);
      setBackupInfo(res.data);
    } catch (err) {
      console.error('Error fetching backup info:', err);
    }
  };

  // Load backup info on mount so date shows on tile
  useEffect(() => {
    // eslint-disable-next-line
    fetchBackupInfo();
  }, []);

  // Generate new backup
  const handleGenerateBackup = async () => {
    setGeneratingBackup(true);
    setBackupMessage('');
    try {
      const res = await axios.post(`${API}/backups/generate`);
      setBackupMessage(`Backup generated! ${res.data.zip_size_mb} MB`);
      await fetchBackupInfo();
    } catch (err) {
      setBackupMessage('Error generating backup');
    } finally {
      setGeneratingBackup(false);
    }
  };

  // Download backup — uses an anchor click + blob URL pattern instead of
  // window.open() so iOS Safari's popup blocker can't kill it.  Falls back to
  // a plain anchor click against the API URL if the blob fetch fails.
  const handleDownloadBackup = async () => {
    const url = `${API}/backups/download`;
    try {
      setBackupMessage('Preparing download…');
      const res = await axios.get(url, { responseType: 'blob', timeout: 120000 });
      const blob = new Blob([res.data], { type: 'application/zip' });
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = (backupInfo && backupInfo.filename) || `ClearAllWizard_${new Date().toISOString().slice(0,10)}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Revoke after a tick so Safari has time to actually start the download
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60_000);
      setBackupMessage('Download started.');
    } catch (err) {
      console.error('[Backup download] blob fetch failed, falling back to direct link', err);
      // Fallback: plain anchor click against the API URL.  Same tab so iOS
      // can't popup-block it.  Works for desktop browsers in all cases.
      const a = document.createElement('a');
      a.href = url;
      a.target = '_self';
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setBackupMessage('If the download didn\'t start, check your downloads folder or try again.');
    }
  };

  // Open backup modal
  const openBackupModal = async () => {
    setShowBackupModal(true);
    setBackupMessage('');
    await fetchBackupInfo();
  };

  // Send Client Intake Link
  const handleSendIntakeLink = async () => {
    if (!intakeEmail) return;
    
    setSendingIntake(true);
    setIntakeError('');
    setIntakeSuccess(false);
    
    try {
      const response = await axios.post(`${API}/client-intake/send-link`, {
        clientEmail: intakeEmail,
        clientName: intakeName
      });
      
      if (response.data.success) {
        setIntakeSuccess(true);
        setTimeout(() => {
          setShowIntakeModal(false);
          setIntakeEmail('');
          setIntakeName('');
          setIntakeSuccess(false);
        }, 2000);
      }
    } catch (err) {
      console.error('Error sending intake link:', err);
      setIntakeError(err.response?.data?.detail || 'Failed to send intake link');
    } finally {
      setSendingIntake(false);
    }
  };

  return (
    <div data-testid="dashboard-page" className="min-h-[80vh] flex flex-col items-center justify-center p-4 md:p-6">
      {/* Client Intake Modal */}
      {showIntakeModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-gray-700 rounded-xl p-6 w-full max-w-md shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-teal-500/20 rounded-lg flex items-center justify-center">
                  <UserPlus className="w-5 h-5 text-teal-400" />
                </div>
                <h3 className="text-xl font-semibold text-white">Send Client Intake Link</h3>
              </div>
              <button 
                onClick={() => {
                  setShowIntakeModal(false);
                  setIntakeEmail('');
                  setIntakeName('');
                  setIntakeError('');
                  setIntakeSuccess(false);
                }}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {intakeSuccess ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="w-8 h-8 text-green-400" />
                </div>
                <p className="text-green-400 font-medium">Intake link sent successfully!</p>
              </div>
            ) : (
              <>
                <p className="text-gray-400 text-sm mb-4">
                  Enter the client&apos;s email address to send them the intake form link. They&apos;ll be able to submit their personal information, upload documents, and provide credit monitoring logins.
                </p>
                
                {intakeError && (
                  <div className="mb-4 p-3 bg-red-900/50 border border-red-500 rounded-lg text-red-300 text-sm">
                    {intakeError}
                  </div>
                )}
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-gray-400 text-sm mb-2">Client Name (optional)</label>
                    <input
                      type="text"
                      value={intakeName}
                      onChange={(e) => setIntakeName(e.target.value)}
                      placeholder="John Smith"
                      className="w-full p-3 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-gray-400 text-sm mb-2">Client Email *</label>
                    <input
                      type="email"
                      value={intakeEmail}
                      onChange={(e) => setIntakeEmail(e.target.value)}
                      placeholder="client@email.com"
                      className="w-full p-3 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      required
                    />
                  </div>
                </div>
                
                <div className="flex gap-3 mt-6">
                  <button
                    onClick={() => {
                      setShowIntakeModal(false);
                      setIntakeEmail('');
                      setIntakeName('');
                      setIntakeError('');
                    }}
                    className="flex-1 px-4 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSendIntakeLink}
                    disabled={!intakeEmail || sendingIntake}
                    className="flex-1 px-4 py-3 bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 disabled:from-gray-600 disabled:to-gray-700 text-white font-semibold rounded-lg transition-all flex items-center justify-center gap-2"
                  >
                    {sendingIntake ? (
                      <>
                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        Send Link
                      </>
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      
      {/* Centered Content */}
      <div className="text-center max-w-4xl w-full">

        {/* Floating side-tile: next-due promised payments */}
        <UpcomingPaymentsTile />

        {/* Backup Modal */}
        {showBackupModal && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-gray-900 border border-gray-700 rounded-xl p-6 w-full max-w-lg shadow-2xl text-left">
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                    <HardDrive className="w-5 h-5 text-purple-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-white">Rebuild Kit</h3>
                </div>
                <button 
                  onClick={() => setShowBackupModal(false)}
                  className="text-gray-400 hover:text-white transition-colors"
                  data-testid="close-backup-modal"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              {backupInfo?.exists && (
                <div className="mb-4 p-3 bg-gray-800/80 border border-gray-700 rounded-lg">
                  <p className="text-gray-300 text-sm">
                    <span className="text-purple-400 font-medium">Last Backup:</span> {backupInfo.backup_date || backupInfo.last_generated}
                  </p>
                  <p className="text-gray-300 text-sm mt-1">
                    <span className="text-purple-400 font-medium">Size:</span> {backupInfo.size_mb} MB &middot; <span className="text-purple-400 font-medium">Files:</span> {backupInfo.file_count}
                  </p>
                </div>
              )}

              {!backupInfo?.exists && (
                <p className="text-gray-400 text-sm mb-4">No backup exists yet. Generate your first backup below.</p>
              )}

              <div className="mb-5 text-sm text-gray-400 space-y-2">
                <p className="text-white font-medium">How it works:</p>
                <p>1. Hit <span className="text-purple-300 font-medium">Save New Backup</span> to snapshot all your code right now</p>
                <p>2. Hit <span className="text-teal-300 font-medium">Download</span> to get the zip file</p>
                <p>3. Inside the zip are <span className="text-white">8 folders (Step 1 through Step 8)</span></p>
                <p>4. Open each folder, copy the code, paste it into Emergent one folder at a time</p>
                <p>5. When all 8 folders are pasted, your app is rebuilt exactly as it was</p>
              </div>

              {backupMessage && (
                <div className={`mb-4 p-3 rounded-lg text-sm ${backupMessage.includes('Error') ? 'bg-red-900/50 border border-red-500 text-red-300' : 'bg-green-900/50 border border-green-500 text-green-300'}`}>
                  {backupMessage}
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={handleGenerateBackup}
                  disabled={generatingBackup}
                  data-testid="generate-backup-btn"
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 disabled:from-gray-600 disabled:to-gray-700 text-white font-semibold rounded-lg transition-all flex items-center justify-center gap-2"
                >
                  {generatingBackup ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4" />
                      Save New Backup
                    </>
                  )}
                </button>
                {backupInfo?.exists && (
                  <button
                    onClick={handleDownloadBackup}
                    data-testid="download-backup-btn"
                    className="flex-1 px-4 py-3 bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white font-semibold rounded-lg transition-all flex items-center justify-center gap-2"
                  >
                    <Download className="w-4 h-4" />
                    Download
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
        {/* Logo and Welcome */}
        <div className="mb-10">
          <img 
            src="/images/clear-all-wizard-logo.png" 
            alt="Clear All Wizard" 
            className="w-24 h-24 md:w-32 md:h-32 object-contain mx-auto mb-4"
          />
          <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">Clear All Wizard</h1>
          <p className="text-zinc-400">Select a tool to get started</p>
        </div>

        {/* Main Action Buttons - 2 rows, centered */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6 max-w-3xl mx-auto">
          {/* Row 1: Primary Tools */}
          <Link to="/dispute-wizard/new" className="col-span-1">
            <Button 
              data-testid="start-wizard-btn" 
              className="w-full h-24 md:h-28 bg-green-600 hover:bg-green-700 flex flex-col items-center justify-center gap-2 rounded-xl text-white shadow-lg shadow-green-900/30 transition-all hover:scale-[1.02]"
            >
              <FileText className="w-8 h-8" />
              <span className="text-base font-semibold">Dispute Wizard</span>
            </Button>
          </Link>
          
          {/* 2026-02-08 — Tapping "FTC Report" from the dashboard must
              always start a BRAND NEW report. Previously the page
              rehydrated sessionStorage and dumped the user back on their
              last half-finished draft. `?fresh=1` triggers a wipe on
              mount inside FTCBuilderPage. */}
          <Link to="/ftc-builder?fresh=1" className="col-span-1">
            <Button 
              data-testid="ftc-builder-btn" 
              className="w-full h-24 md:h-28 bg-blue-600 hover:bg-blue-700 flex flex-col items-center justify-center gap-2 rounded-xl text-white shadow-lg shadow-blue-900/30 transition-all hover:scale-[1.02]"
            >
              <FileText className="w-8 h-8" />
              <span className="text-base font-semibold">FTC Report</span>
            </Button>
          </Link>
          
          {/* Row 2: Secondary Tools */}
          <Link to="/pre-screening" className="col-span-1">
            <Button 
              data-testid="pre-screening-btn" 
              className="w-full h-24 md:h-28 bg-amber-600 hover:bg-amber-700 flex flex-col items-center justify-center gap-2 rounded-xl text-white shadow-lg shadow-amber-900/30 transition-all hover:scale-[1.02]"
            >
              <Search className="w-8 h-8" />
              <span className="text-base font-semibold">Pre-Screening</span>
            </Button>
          </Link>
          
          <Button 
            data-testid="promise-to-pay-btn" 
            onClick={() => setShowPromiseToPayModal(true)}
            className="w-full h-24 md:h-28 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 flex flex-col items-center justify-center gap-2 rounded-xl text-white shadow-lg shadow-emerald-900/30 transition-all hover:scale-[1.02]"
          >
            <DollarSign className="w-8 h-8" />
            <span className="text-base font-semibold">Promise to Pay</span>
          </Button>
          
          <Link to="/client-progress" className="col-span-1">
            <Button 
              data-testid="client-progress-btn" 
              className="w-full h-24 md:h-28 bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 flex flex-col items-center justify-center gap-2 rounded-xl text-white shadow-lg shadow-teal-900/30 transition-all hover:scale-[1.02]"
            >
              <TrendingUp className="w-8 h-8" />
              <span className="text-base font-semibold">Client Progress</span>
            </Button>
          </Link>

          {/* Client Updates — inbox for status updates submitted via the public link.
              Badge shows unread count; footer strip shows avg score-delta per bureau. */}
          <Link to="/client-updates" className="col-span-1 relative">
            <Button
              data-testid="client-updates-btn"
              className="w-full h-24 md:h-28 bg-gradient-to-r from-sky-600 to-blue-600 hover:from-sky-700 hover:to-blue-700 flex flex-col items-center justify-center gap-1 rounded-xl text-white shadow-lg shadow-sky-900/30 transition-all hover:scale-[1.02] relative overflow-hidden"
            >
              <MessageSquare className="w-7 h-7" />
              <span className="text-base font-semibold">Client Updates</span>
              {cuStats && cuStats.totalCount > 0 && (
                <span className="text-[10px] text-sky-100 opacity-90">
                  {cuStats.totalCount} submission{cuStats.totalCount === 1 ? '' : 's'}
                </span>
              )}
              {/* Score-delta strip */}
              {cuStats && (cuStats.avgDelta?.experian != null || cuStats.avgDelta?.equifax != null || cuStats.avgDelta?.transunion != null) && (
                <div className="absolute bottom-0 left-0 right-0 bg-black/25 backdrop-blur-sm px-1.5 py-0.5 flex items-center justify-around text-[10px] font-semibold" data-testid="cu-score-delta-strip">
                  <BureauDelta label="EX" value={cuStats.avgDelta.experian} />
                  <BureauDelta label="EQ" value={cuStats.avgDelta.equifax} />
                  <BureauDelta label="TU" value={cuStats.avgDelta.transunion} />
                </div>
              )}
            </Button>
            {/* Unread badge — floats above the tile, mirrors mail-app conventions */}
            {cuStats && cuStats.unreadCount > 0 && (
              <span
                data-testid="cu-dashboard-unread-badge"
                className="absolute -top-1.5 -right-1.5 min-w-[24px] h-6 px-1.5 rounded-full bg-red-500 text-white text-xs font-bold flex items-center justify-center shadow-lg ring-2 ring-gray-900 z-10"
              >
                {cuStats.unreadCount > 99 ? '99+' : cuStats.unreadCount}
              </span>
            )}
          </Link>

          <Link to="/moms-house" className="col-span-1">
            <Button 
              data-testid="moms-house-btn" 
              className="w-full h-24 md:h-28 bg-gradient-to-r from-pink-600 to-rose-600 hover:from-pink-700 hover:to-rose-700 flex flex-col items-center justify-center gap-2 rounded-xl text-white shadow-lg shadow-pink-900/30 transition-all hover:scale-[1.02]"
            >
              <Home className="w-8 h-8" />
              <span className="text-base font-semibold">Mom&apos;s House</span>
            </Button>
          </Link>

          <Link to="/monthly-bills" className="col-span-1">
            <Button 
              data-testid="monthly-bills-btn" 
              className="w-full h-24 md:h-28 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 flex flex-col items-center justify-center gap-2 rounded-xl text-white shadow-lg shadow-emerald-900/30 transition-all hover:scale-[1.02]"
            >
              <Receipt className="w-8 h-8" />
              <span className="text-base font-semibold">Monthly Bills</span>
            </Button>
          </Link>

          {/* 2026-02-24 — To-Do List removed at user request (see PRD) —
              the daily reminder emails couldn't be reliably muted from the
              UI so the whole section was pulled from the app.  The
              underlying routes still exist for data-safety but the
              dashboard tile + navigation are gone. */}

          <Link to="/tradeline-world" className="col-span-1">
            <Button
              data-testid="tradeline-world-btn"
              className="w-full h-24 md:h-28 bg-gradient-to-r from-fuchsia-600 to-pink-600 hover:from-fuchsia-700 hover:to-pink-700 flex flex-col items-center justify-center gap-2 rounded-xl text-white shadow-lg shadow-fuchsia-900/30 transition-all hover:scale-[1.02]"
            >
              <CreditCard className="w-8 h-8" />
              <span className="text-base font-semibold">Tradeline World</span>
            </Button>
          </Link>

          {/* 2026-02-XX (user request) — Sports Betting Parlay Tracker tile.
              Private side-tool: upload FanDuel screenshots → AI OCR → live
              scores from ESPN → P&L chart.  PIN-locked (same keypad style
              as the Paystub wizard). */}
          <Link to="/sports-betting" state={{ fromDashboard: true }} className="col-span-1">
            <Button
              data-testid="sports-betting-btn"
              className="w-full h-24 md:h-28 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 flex flex-col items-center justify-center gap-2 rounded-xl text-white shadow-lg shadow-amber-900/30 transition-all hover:scale-[1.02]"
            >
              <Trophy className="w-8 h-8" />
              <span className="text-base font-semibold">Sports Betting</span>
            </Button>
          </Link>
          
          <Button 
            data-testid="backup-btn" 
            onClick={openBackupModal}
            className="w-full h-24 md:h-28 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 flex flex-col items-center justify-center gap-1 rounded-xl text-white shadow-lg shadow-purple-900/30 transition-all hover:scale-[1.02] col-span-1"
          >
            <HardDrive className="w-7 h-7" />
            <span className="text-base font-semibold">Backup Files</span>
            {backupInfo?.exists && backupInfo?.backup_date && (
              <span className="text-xs text-purple-200 opacity-80">Last: {backupInfo.backup_date}</span>
            )}
          </Button>
        </div>
      </div>

      {/* Promise to Pay Modal */}
      <PromiseToPayModal 
        isOpen={showPromiseToPayModal} 
        onClose={() => setShowPromiseToPayModal(false)} 
      />
    </div>
  );
}

/** Tiny inline component for the per-bureau score-change strip on the
 *  Client Updates tile.  Green up-arrow when positive, red text when the
 *  score dropped, gray "–" when no sample yet. */
function BureauDelta({ label, value }) {
  if (value == null) {
    return (
      <span className="flex items-center gap-0.5 text-white/60">
        <span>{label}</span><span>–</span>
      </span>
    );
  }
  const positive = value > 0;
  const zero = value === 0;
  const color = zero ? 'text-white/70' : positive ? 'text-emerald-300' : 'text-rose-300';
  const sign = positive ? '+' : '';
  return (
    <span className={`flex items-center gap-0.5 ${color}`}>
      <span className="text-white/80">{label}</span>
      {positive && !zero && <ArrowUp className="w-2.5 h-2.5" />}
      <span>{sign}{value}</span>
    </span>
  );
}
